import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Section,
  Input,
  Textarea,
  Button,
  Text,
  Placeholder,
} from '@telegram-apps/telegram-ui';
import { useTelegram } from '../hooks/useTelegram';
import { ContactManagerLink } from '../components/ContactManagerLink';
import { api } from '../api/client';

/** Максимальный размер файла — 10МБ */
const MAX_FILE_SIZE = 10 * 1024 * 1024;
const MAX_FILES = 5;

/** Форматирование размера файла */
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export function CreateDealPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setBackButtonVisible, hapticFeedback } = useTelegram();
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Предвыбор категории из query param (?category=0 или ?category=7)
  const presetCategory = searchParams.get('category');
  const [categoryId, setCategoryId] = useState<number>(
    presetCategory !== null ? parseInt(presetCategory, 10) : 0
  );
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [newDealId, setNewDealId] = useState<number | null>(null);

  // Кнопка "Назад"
  useEffect(() => {
    setBackButtonVisible(true, () => navigate(-1));
    return () => setBackButtonVisible(false);
  }, [setBackButtonVisible, navigate]);

  /** Добавить файлы */
  function handleFilesSelected(e: React.ChangeEvent<HTMLInputElement>) {
    const selected = Array.from(e.target.files || []);
    if (!selected.length) return;

    const totalFiles = files.length + selected.length;
    if (totalFiles > MAX_FILES) {
      setError(`Максимум ${MAX_FILES} файлов`);
      return;
    }

    const oversized = selected.find(f => f.size > MAX_FILE_SIZE);
    if (oversized) {
      setError(`Файл "${oversized.name}" превышает ${formatFileSize(MAX_FILE_SIZE)}`);
      return;
    }

    setError('');
    setFiles(prev => [...prev, ...selected]);
    // Сбрасываем input чтобы можно было повторно выбрать тот же файл
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  /** Удалить файл по индексу */
  function removeFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }

  async function handleSubmit() {
    if (!title.trim()) {
      setError('Введите тему заявки');
      return;
    }
    if (!description.trim()) {
      setError('Опишите задачу');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const result = await api.deals.create({
        title: title.trim(),
        description: description.trim(),
        categoryId,
        files,
      });

      hapticFeedback('success');
      setNewDealId(result.dealId);
      setSuccess(true);
    } catch (err) {
      hapticFeedback('error');
      setError(err instanceof Error ? err.message : 'Ошибка создания заявки');
    } finally {
      setIsLoading(false);
    }
  }

  // Успешное создание
  if (success && newDealId) {
    return (
      <Placeholder
        header="Заявка создана!"
        description={`Заявка #${newDealId} успешно зарегистрирована. Мы приступим к обработке в ближайшее время.`}
        action={
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <Button size="l" onClick={() => navigate(`/deal/${newDealId}`)}>
              Открыть заявку
            </Button>
            <Button size="l" mode="outline" onClick={() => navigate('/')}>
              К списку заявок
            </Button>
          </div>
        }
      />
    );
  }

  return (
    <div style={{ padding: '16px' }}>
      <Text weight="1" style={{ fontSize: '20px', marginBottom: '16px' }}>
        Новая заявка
      </Text>

      {/* Тип заявки */}
      <div style={{ marginBottom: '16px' }}>
        <Text style={{ fontSize: '14px', color: 'var(--tgui--hint_color)', marginBottom: '8px' }}>
          Тип заявки
        </Text>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <button
            type="button"
            onClick={() => setCategoryId(0)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              padding: '14px 16px',
              borderRadius: '12px',
              border: categoryId === 0 ? '2px solid var(--stotek-red, #EC2226)' : '2px solid var(--tgui--divider_color, #e0e0e0)',
              backgroundColor: categoryId === 0 ? 'rgba(236, 34, 38, 0.08)' : 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              color: 'inherit',
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 600 }}>
              Что-то не работает / нужна помощь
            </span>
            <span style={{ fontSize: '13px', color: 'var(--tgui--hint_color, #999)' }}>
              Техническая поддержка
            </span>
          </button>

          <button
            type="button"
            onClick={() => setCategoryId(7)}
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              padding: '14px 16px',
              borderRadius: '12px',
              border: categoryId === 7 ? '2px solid var(--stotek-red, #EC2226)' : '2px solid var(--tgui--divider_color, #e0e0e0)',
              backgroundColor: categoryId === 7 ? 'rgba(236, 34, 38, 0.08)' : 'transparent',
              textAlign: 'left',
              cursor: 'pointer',
              WebkitTapHighlightColor: 'transparent',
              color: 'inherit',
            }}
          >
            <span style={{ fontSize: '16px', fontWeight: 600 }}>
              Нужна доработка или новая функция
            </span>
            <span style={{ fontSize: '13px', color: 'var(--tgui--hint_color, #999)' }}>
              Доработка 1С, Битрикс 24
            </span>
          </button>
        </div>
      </div>

      {/* Форма */}
      <Section>
        <Input
          header="Тема *"
          placeholder="Кратко опишите задачу"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
        />
      </Section>

      <Section>
        <Textarea
          header="Описание *"
          placeholder="Подробно опишите, что нужно сделать..."
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          style={{ minHeight: '120px' }}
        />
      </Section>

      {/* Зона файлов */}
      <div style={{ marginTop: '12px' }}>
        <Text style={{ fontSize: '14px', color: 'var(--tgui--hint_color)', marginBottom: '8px' }}>
          Файлы (необязательно)
        </Text>

        {/* Список прикреплённых файлов */}
        {files.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '10px' }}>
            {files.map((file, index) => (
              <div
                key={`${file.name}-${index}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '8px 12px',
                  borderRadius: '10px',
                  backgroundColor: 'var(--tgui--secondary_bg_color, #f5f5f5)',
                }}
              >
                {/* Иконка файла или превью */}
                {file.type.startsWith('image/') ? (
                  <img
                    src={URL.createObjectURL(file)}
                    alt={file.name}
                    style={{ width: '36px', height: '36px', borderRadius: '6px', objectFit: 'cover' }}
                  />
                ) : (
                  <div style={{
                    width: '36px',
                    height: '36px',
                    borderRadius: '6px',
                    backgroundColor: 'var(--tgui--link_color, #2196F3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: '12px',
                    fontWeight: 600,
                    flexShrink: 0,
                  }}>
                    {file.name.split('.').pop()?.toUpperCase().slice(0, 3) || 'F'}
                  </div>
                )}

                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontSize: '13px',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}>
                    {file.name}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--tgui--hint_color, #999)' }}>
                    {formatFileSize(file.size)}
                  </div>
                </div>

                {/* Кнопка удалить */}
                <button
                  onClick={() => removeFile(index)}
                  style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    border: 'none',
                    backgroundColor: 'rgba(255, 59, 48, 0.1)',
                    color: '#ff3b30',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        )}

        {/* Кнопки прикрепления */}
        {files.length < MAX_FILES && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.zip,.rar"
              onChange={handleFilesSelected}
              style={{ display: 'none' }}
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                padding: '10px',
                borderRadius: '10px',
                border: '1px dashed var(--tgui--divider_color, #ccc)',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '13px',
                color: 'var(--tgui--link_color, #2196F3)',
                WebkitTapHighlightColor: 'transparent',
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
              </svg>
              Прикрепить файл
            </button>
          </div>
        )}

        <Text style={{ fontSize: '11px', color: 'var(--tgui--hint_color, #999)', marginTop: '6px' }}>
          До {MAX_FILES} файлов, макс. {formatFileSize(MAX_FILE_SIZE)} каждый
        </Text>
      </div>

      {error && (
        <Text style={{ color: 'var(--tgui--destructive_text_color)', padding: '8px 0', fontSize: '14px' }}>
          {error}
        </Text>
      )}

      <div style={{ padding: '16px 0' }}>
        <Button
          size="l"
          stretched
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!title.trim() || !description.trim() || isLoading}
        >
          Создать заявку
        </Button>
      </div>

      <ContactManagerLink />
    </div>
  );
}
